import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import colors from 'config/colors';

import * as templateThunks from 'thunks/templates';

import { MdPushPinOutline } from 'components/Icons';
import PageHeader from 'components/PageHeader';
import SplitButton from 'components/SplitButton';
import { FilterBarV2 } from 'components/FilterBar';
import { useFeatureFlag } from 'hooks/feature';
import MonthCalendarKey from '../MonthCalendar/MonthCalendarKey';
import TimePeriodControl from './TimePeriodControl';
import ShiftSearchBox from './ShiftSearchBox';
import { CreateShiftButton, ExportShiftsButton, PinShiftsPanelButton } from './ShiftsHeaderButtons';

const styles = {
  outerContainer: {
    display: 'flex',
    backgroundColor: colors.dagny.backgroundLight6,
    flexWrap: 'wrap',
  },
  rightContainer: {
    flex: '1 1 300px',
    minWidth: '300px',
    maxWidth: '400px',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: colors.dagny.backgroundLight6,
  },
  leftContainer: {
    position: 'relative',
    flex: '1 1 600px',
    minWidth: '500px',
    borderRight: `1px solid ${colors.dagny.line}`,
    backgroundColor: colors.dagny.backgroundLight6,
  },
  topLeftContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    alignItems: 'center',
    borderBottom: `1px solid ${colors.dagny.line}`,
    padding: '12px',
    backgroundColor: colors.dagny.backgroundLight6,
  },
  headerSection: {
    flex: '0 0 auto',
    marginRight: '24px',
  },
  contentWrapper: {
    flex: '1 1 auto',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    justifyContent: 'flex-end',
  },
  periodSection: {
    flex: '1 1 300px',
    minWidth: '200px',
    marginRight: 'auto',
  },
  buttonSection: {
    display: 'flex',
    gap: '6px',
    flex: '0 1 auto',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
  },
  searchSection: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    paddingTop: '12px',
    paddingLeft: '12px',
    alignItems: 'center',
  },
  searchBox: {
    flex: '1 1 200px',
    minWidth: '150px',
  },
  exportButton: {
    flex: '0 0 auto',
    paddingRight: '6px',
  },
  pinSection: {
    display: 'flex',
    paddingTop: '6px',
    paddingLeft: '12px',
    alignItems: 'center',
    gap: '12px',
  },
  pinTip: {
    flex: 1,
    color: colors.text,
    paddingRight: '6px',
    fontSize: '0.70rem',
    lineHeight: 1.2,
  },
};

export default function ShiftsHeader(props) {
  const dispatch = useDispatch();
  const isMobile = useSelector(state => state.screen.isMobile);
  const customTerminology = useSelector(state => state.global.orgConfig?.customTerminology);
  const adminCanCreateShifts = useFeatureFlag(null, 'jobsCanCreate');
  const isSupplier = useFeatureFlag('supplier');

  const createBlankTemplate = (periodType) => {
    dispatch(templateThunks.createBlankTemplate(periodType));
    props.replaceToTemplateEditView('new');
  };

  const splitButtonMenuItems = [
    { key: 'view-templates', label: 'View Templates', onClick: props.goToTemplatesListView },
    { key: 'new-day-template', label: 'New Day Template', onClick: () => createBlankTemplate('day') },
    { key: 'new-week-template', label: 'New Week Template', onClick: () => createBlankTemplate('week') },
  ];

  const filterBarButtons = (
    <div style={styles.buttonSection}>
      <CreateShiftButton period={props.period} goToShiftTab={props.goToShiftTab} />
      {adminCanCreateShifts && (
        <SplitButton
          menuItems={splitButtonMenuItems}
          onClick={() => props.gotToApplyTemplate(props.period.startDate)}
          openMenuDirection="left"
          white
        >
          Apply Template
        </SplitButton>
      )}
    </div>
  );

  return (
    <div className="shiftHeader" style={styles.outerContainer}>
      <div style={styles.leftContainer}>
        <div style={styles.topLeftContainer}>
          <div className="m-hide" style={styles.headerSection}>
            <PageHeader
              title={customTerminology?.shifts ?? 'Shifts'}
              linkTo="/shifts"
              style={{ marginLeft: 6, marginTop: 6, marginBottom: 6 }}
            />
          </div>

          <div style={styles.contentWrapper}>
            <div style={styles.periodSection}>
              <TimePeriodControl period={props.period} goToDate={props.goToDate} />
            </div>

            <div className="filterBarButtonContainer m-hide" style={styles.buttonSection}>
              <CreateShiftButton period={props.period} goToShiftTab={props.goToShiftTab} />
              {adminCanCreateShifts && (
                <SplitButton
                  menuItems={splitButtonMenuItems}
                  onClick={() => props.gotToApplyTemplate(props.period.startDate)}
                  openMenuDirection="left"
                  white
                >
                  Apply Template
                </SplitButton>
              )}
            </div>
          </div>
        </div>

        <div style={{ padding: 6, backgroundColor: colors.dagny.backgroundLight6 }}>
          <FilterBarV2
            namespace="shifts"
            filterConfigKey="enabledShiftFilters"
            buttons={filterBarButtons}
          />
          {!isSupplier && <MonthCalendarKey />}
        </div>
      </div>

      <div style={styles.rightContainer}>
        <div style={styles.searchSection}>
          <div style={styles.searchBox}>
            <ShiftSearchBox expand fullWidth />
          </div>
          <div style={styles.exportButton}>
            <ExportShiftsButton period={props.period} />
          </div>
        </div>

        <div style={styles.pinSection}>
          <PinShiftsPanelButton />
          <p className="m-hide" style={styles.pinTip}>
            Tip: Use the <MdPushPinOutline /> button to prevent the shift details panel from covering the calendar or list view.
          </p>
        </div>
      </div>
    </div>
  );
}

const { func, object } = PropTypes;
ShiftsHeader.propTypes = {
  period: object.isRequired,
  goToDate: func.isRequired,
  goToShiftTab: func.isRequired,
  gotToApplyTemplate: func.isRequired,
  goToTemplatesListView: func.isRequired,
  replaceToTemplateEditView: func.isRequired,
};
